'use client';
import { MainLogo } from '@modules/common/components/logo/main-logo';
import { Facebook } from '@modules/common/icons/facebook';
import { Instagram } from '@modules/common/icons/instagram';
import { LinkedIn } from '@modules/common/icons/linkedIn';
import Link from 'next/link';
import './footer-nav.scss';
import { usePathname } from 'next/navigation';
import { routes } from '@lib/constants/routes-constant';
import { useState, useEffect } from 'react';

type FooterNavProps = {
  variant: 'light' | 'dark';
};

const FooterNav = ({ variant = 'light' }: FooterNavProps) => {
  const pathname = usePathname();
  const [appVersion, setAppVersion] = useState<string>();

  useEffect(() => {
    setAppVersion(localStorage?.getItem('app_version') || '');
  }, []);

  return (
    <div className="footer">
      <div className={`footer-content ${variant == 'dark' ? 'footer-dark' : 'footer-light'}`}>
        <div className="footerEllipse">
          <div className="footerEllipse-vector"></div>
        </div>
        <div className="footer-top">
          <MainLogo className="w-[147px]" variant={variant === 'dark' ? 'light' : 'dark'} />
        </div>
        <div className="text-center block h-10 md:mt-10">
          <Link
            href="/contact-us"
            prefetch={false}
            className="bg-[#6CC6B1] text-[11px] md:text-base font-bold rounded-full px-6 pt-[9px] pb-1.5 mx-auto"
          >
            Contact Us
          </Link>
        </div>
        <div className="socialNetwork">
          <div className="socialNetwork-item">
            <a href="https://www.instagram.com/giftcardmarket_/" target="_blank">
              <Instagram />
            </a>
          </div>
          <div className="socialNetwork-item">
            <a
              href="https://www.facebook.com/profile.php?id=61555658974395&notif_id=1721828713025117&notif_t=page_user_activity&ref=notif"
              target="_blank"
            >
              <Facebook />
            </a>
          </div>
          <div className="socialNetwork-item">
            <a href="https://www.linkedin.com/company/the-gift-card-market/" target="_blank">
              <LinkedIn />
            </a>
          </div>
        </div>
        {pathname !== routes.temporarily_unavailable && (
          <div className={`footerNav ${variant == 'dark' ? 'footerNav-dark' : 'footerNav-light'}`}>
            <div className="footerNav-item">
              <Link href="/terms-and-conditions" prefetch={false}>
                Terms
              </Link>
            </div>
            <div className="footerNav-item">
              <Link href="/privacy-policy" prefetch={false}>
                Privacy
              </Link>
            </div>
            <div className="footerNav-item">
              <Link href="/faqs" prefetch={false}>
                FAQs
              </Link>
            </div>
            <div className="footerNav-item">
              <Link href="/contact-us" prefetch={false}>
                Contact
              </Link>
            </div>
          </div>
        )}

        <p className="text-[11px] md:text-sm font-medium max-w-[1321px] px-4 text-center mx-auto pb-[31px] flex flex-col gap-4 text-[#FFFFFF4D]">
          <span>
            Multiple Patents Pending © 2025 Gift Card Market™, LLC. All rights reserved. Technology and systems used on
            this site are the property of Gift Card Market and are made available under license. Unauthorized use is
            prohibited.
          </span>
          <span>
            *Our Local Gift Cards are a Prepaid Gift Card product that can be used within a specific category of
            merchants, i.e., restaurants, spas, or salons.
          </span>
          <span>
            Gift Card Market LLC, is issued by Sunrise Banks N.A., Member FDIC, pursuant to a license from Mastercard
            International Incorporated. Mastercard is a registered trademark, and the circles design is a trademark of
            Mastercard International Incorporated. This card may be used everywhere Debit Mastercard is accepted. Use of
            this card constitutes acceptance of the terms and conditions stated in the Cardholder Agreements.
          </span>
          <span>
            Which Cardholder Agreement Applies to You? If you bought your gift card at a store or online for personal
            use, refer to{' '}
            <a
              href="https://gcm.krna.co/wp-content/uploads/2025/05/Cardholder_Agreement_Giftcardmarket.pdf"
              target="_blank"
              className="underline"
            >
              The Gift Card Market Mastercard® Cardholder Agreement
            </a>
            . If you received your card through a business or part of a points redemption program, refer to the{' '}
            <a
              href="https://gcm.krna.co/wp-content/uploads/2025/05/GCMINCENTVIRT.CARDHOLDERAGREEMENT_0525_v3-NG-PDF.pdf"
              target="_blank"
              className="underline"
            >
              Gift Card Market Incentive Mastercard® Cardholder Agreement
            </a>
            . If you received your card as part of a promotion, giveaway, or special event and it has a shorter
            expiration date, refer to the{' '}
            <a
              href="https://gcm.krna.co/wp-content/uploads/2025/08/GCM_PROMONOATMVIRTUAL.CARDHOLDERAGREEMENT_0825-1.pdf"
              target="_blank"
              className="underline"
            >
              Gift Card Market Promotional Mastercard® Cardholder Agreement
            </a>
            .
          </span>
          <span>
            Card program marketed and administered by Usio Inc. Usio Inc. is an Independent Sales Organization (ISO)
            pursuant to an agreement with Sunrise Banks, N.A.{' '}
            <a
              href="https://www.giftcardmarket.com/policies/Sunrise-Banks-Privacy-Notice-Safe-Harbor.pdf"
              target="_blank"
              className="underline"
            >
              Sunrise Banks Privacy Policy
            </a>
          </span>
        </p>
        <p className="text-xs md:text-xs px-4 text-right mx-auto opacity-20">Version: {appVersion}</p>
      </div>
    </div>
  );
};

export default FooterNav;
